import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, ArrowLeft, Search } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NbInvoiceForm } from "@/components/billing/NbInvoiceForm";
import { NbInvoiceDetail } from "@/components/billing/NbInvoiceDetail";
import { NB_STATUS_LABEL, tanggalId } from "@/lib/invoicePrint";

const rp = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");
const STATUS_CLS = { BELUM_LUNAS: "bg-[#FEF2F2] text-[#B91C1C]", SEBAGIAN: "bg-[#FFFBEB] text-[#B45309]", LUNAS: "bg-[#ECFDF5] text-[#047857]", DIBATALKAN: "bg-[#F3F4F6] text-[#6B7280]" };

const NbList = ({ onOpen }) => {
  const [f, setF] = useState({ q: "", periode: "", status: "" });
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const load = useCallback(() => {
    const params = { page, limit: 20, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)) };
    api.get("/billing/nb-invoices", { params }).then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gagal memuat invoice"));
  }, [f, page]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const set = (k) => (e) => { setPage(1); setF({ ...f, [k]: e.target.value }); };
  return (
    <div className="space-y-3" data-testid="nb-list">
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1"><Search className="absolute left-2 top-2.5 h-4 w-4 text-[#9CA3AF]" />
          <Input className="pl-8" placeholder="Cari nomor invoice, nama, atau ID pelanggan" value={f.q} onChange={set("q")} data-testid="nb-search" /></div>
        <Input type="month" className="sm:w-[170px]" value={f.periode} onChange={set("periode")} data-testid="nb-filter-periode" />
        <select className="h-9 rounded-md border px-2 text-sm" value={f.status} onChange={set("status")} data-testid="nb-filter-status">
          <option value="">Semua Status</option>{Object.entries(NB_STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
      </div>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-[#F9FAFB] text-left text-xs text-[#4B5563]"><tr><th className="p-2">Nomor</th><th className="p-2">Tanggal</th><th className="p-2">Pelanggan</th><th className="p-2 text-right">Total</th><th className="p-2 text-right">Sisa</th><th className="p-2">Status</th></tr></thead>
          <tbody>
            {data.items.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-xs text-[#6B7280]" data-testid="nb-empty">Belum ada invoice barang/jasa.</td></tr>}
            {data.items.map((i) => (
              <tr key={i.id} className="cursor-pointer border-t hover:bg-[#F9FAFB]" onClick={() => onOpen(i.id)} data-testid={`nb-row-${i.nomor}`}>
                <td className="p-2 font-mono text-xs">{i.nomor}</td><td className="p-2 text-xs">{tanggalId(i.tanggal_invoice)}</td>
                <td className="p-2"><b>{i.customer_name}</b> <span className="font-mono text-xs text-[#6B7280]">{i.customer_id}</span></td>
                <td className="p-2 text-right font-mono">{rp(i.total)}</td><td className="p-2 text-right font-mono">{rp(i.sisa)}</td>
                <td className="p-2"><span className={`rounded px-1.5 py-0.5 text-xs font-bold ${STATUS_CLS[i.status]}`}>{NB_STATUS_LABEL[i.status]}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-xs text-[#6B7280]">
        <span data-testid="nb-total-count">Total {data.total} invoice</span>
        <div className="flex gap-2"><Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Sebelumnya</Button>
          <Button size="sm" variant="outline" disabled={page * 20 >= data.total} onClick={() => setPage(page + 1)}>Berikutnya</Button></div>
      </div>
    </div>
  );
};

export const NbInvoiceDialog = ({ onClose, initialId }) => {
  const [view, setView] = useState({ mode: "list" });
  const [branding, setBranding] = useState({});
  useEffect(() => { api.get("/settings/invoice-branding").then((r) => setBranding(r.data || {})).catch(() => {}); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (initialId) open(initialId); }, [initialId]);
  const open = (id) => api.get(`/billing/nb-invoices/${id}`).then((r) => setView({ mode: "detail", inv: r.data }))
    .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gagal membuka invoice"));
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto" data-testid="nb-dialog">
        <DialogHeader>
          <DialogTitle>Invoice Barang / Jasa</DialogTitle>
          <DialogDescription>Invoice non-bulanan untuk pembelian perangkat, material, dan jasa tambahan. Terpisah dari tagihan internet bulanan.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          {view.mode !== "list" && <Button size="sm" variant="outline" onClick={() => setView({ mode: "list" })} data-testid="nb-back"><ArrowLeft className="mr-1 h-4 w-4" />Daftar</Button>}
          {view.mode === "list" && <Button size="sm" onClick={() => setView({ mode: "form" })} className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="nb-new"><Plus className="mr-1 h-4 w-4" />Buat Invoice Baru</Button>}
        </div>
        {view.mode === "list" && <NbList onOpen={open} />}
        {view.mode === "form" && <NbInvoiceForm initial={view.inv} onCancel={() => (view.inv ? open(view.inv.id) : setView({ mode: "list" }))} onSaved={(d) => open(d.id)} />}
        {view.mode === "detail" && <NbInvoiceDetail inv={view.inv} branding={branding} onChanged={() => open(view.inv.id)} onEdit={() => setView({ mode: "form", inv: view.inv })} />}
      </DialogContent>
    </Dialog>
  );
};
